'use client'

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Dialog, DialogPanel } from '@headlessui/react';
import { Bars3Icon, XMarkIcon, CheckIcon, ArrowDownIcon, UsersIcon, StarIcon, CalendarIcon, BriefcaseIcon } from '@heroicons/react/24/outline';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { saveQuizSubmission } from '../services/firebase';
import InfiniteLogoSlider from '../components/common/InfiniteLogoSlider';
import { useAuthContext } from '../hooks/useAuthContext';
import WaitlistFlowContainer from '../components/features/landing/WaitlistFlowContainer';
import { getFunctions, httpsCallable } from 'firebase/functions';

const navigation = [
  { name: 'FAQ', href: '#faq' },
];

const features = [
  {
    name: 'Vetted Introductions',
    description: 'Get warm introductions to decision-makers, powered by a network of trusted connectors.',
    icon: UsersIcon,
    status: 'Active',
    isActive: true,
  },
  {
    name: 'Exclusive Community',
    description: 'Engage with a curated group of peers who have been vetted for their achievements and ambition.',
    icon: StarIcon,
    status: 'Active',
    isActive: true,
  },
  {
    name: 'Curated Job Board',
    description: 'Access high-impact roles at top companies, often not advertised elsewhere.',
    icon: BriefcaseIcon,
    status: 'Coming Soon',
    isActive: false,
  },
  {
    name: 'Private Events',
    description: 'Attend exclusive online and in-person events with industry leaders and fellow members.',
    icon: CalendarIcon,
    status: 'Coming Soon',
    isActive: false,
  },
];

const companyLogos = [
  { name: 'Airtable', src: '/img/logos/airtable-logo.jpeg' },
  { name: 'Appian', src: '/img/logos/appian-logo.png' },
  { name: 'AWS', src: '/img/logos/aws-logo.png' },
  { name: 'Carlyle', src: '/img/logos/carlyle-logo.png' },
  { name: 'Databricks', src: '/img/logos/databricks-logo.png' },
  { name: 'Deloitte', src: '/img/logos/deloitte-logo-.png' },
  { name: 'J.P. Morgan', src: '/img/logos/jpmorgan-logo.png' },
  { name: 'McKinsey', src: '/img/logos/mckinsey-logo.png' },
  { name: 'OpenAI', src: '/img/logos/openai-logo.jpeg' },
  { name: 'SpaceX', src: '/img/logos/SpaceX-logo.svg' },
  { name: 'Stripe', src: '/img/logos/stripe-logo.png' },
];



const faqs = [
  {
    question: 'How do I join Burnt?',
    answer: 'The process is simple: (1) Apply to join by submitting your profile for review, (2) Get vetted by our committee who carefully reviews every application to maintain network quality, and (3) Once approved, unlock access to the network and start making high-value connections.',
  },
  {
    question: 'What is the criteria to be accepted into Burnt?',
    answer: 'We look for demonstrated excellence, ambition, and a track record of high performance in your field. While there\'s no single formula, we prioritize individuals who will both gain from and contribute to the network\'s quality.',
  },
  {
    question: 'I used the old version of Burnt. Do I need to re-apply?',
    answer: 'Thank you for being an early adopter! Join the waitlist with the same email, and you\'ll receive access for the Burnt 2.0 private beta.',
  },
  {
    question: 'Is Burnt free?',
    answer: 'The private beta will be free for all founding members. We plan to introduce membership tiers in the future.',
  },
  {
    question: 'How is this different from LinkedIn?',
    answer: 'Burnt is a private, vetted network focused on signal, not noise. Every member is a top performer, ensuring all connections and opportunities are high-quality and relevant.',
  },
];

const pricing = {
  name: 'Founder Access',
  price: '$499',
  description: 'Exclusive access for founding members with premium benefits.',
  features: [
    'VIP Access to all private events',
    '3 curated introductions per month',
    'Priority access to job opportunities',
    'Direct access to network connectors',
    'Exclusive founder community access',
    'Personal networking concierge',
  ],
};

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const NewLandingPage = () => {
  const { user, userProfile, authIsReady, profileLoading } = useAuthContext();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isWaitlistSignupInProgress, setIsWaitlistSignupInProgress] = useState(false);

  const [showSuccess, setShowSuccess] = useState(() => {
    // Check sessionStorage for persisted success state
    const persistedSuccess = sessionStorage.getItem('waitlistSignupSuccess');
    if (persistedSuccess) {
      // Clear it after reading so it only shows once
      sessionStorage.removeItem('waitlistSignupSuccess');
      return true;
    }
    return false;
  });
  const [openFaq, setOpenFaq] = useState(null);
  const [flowError, setFlowError] = useState('');
  const navigate = useNavigate();

  const handleWaitlistFlowComplete = (userData) => {
    console.log('Waitlist flow completed:', userData);
    
    // Mark that we're in the middle of waitlist signup to prevent redirects
    setIsWaitlistSignupInProgress(true);
    
    // Show success confirmation on the landing page
    // User is NOT signed in - account was created but they were immediately signed out
    // Persist to sessionStorage so it survives the auth state change re-render
    sessionStorage.setItem('waitlistSignupSuccess', 'true');
    setShowSuccess(true);
    
    // Clear the flag after a short delay
    setTimeout(() => {
      setIsWaitlistSignupInProgress(false);
    }, 100);
  };

  const handleWaitlistFlowError = (error) => {
    console.error('Waitlist flow error:', error);
    setFlowError(error);
  };

  // Scroll to waitlist section when success message appears
  useEffect(() => {
    if (showSuccess) {
      const waitlistSection = document.getElementById('waitlist');
      if (waitlistSection) {
        waitlistSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [showSuccess]);

  // Redirect logged-in users to appropriate page
  useEffect(() => {
    // Don't redirect if we're in the middle of waitlist signup
    if (isWaitlistSignupInProgress) return;
    
    if (authIsReady && user && !profileLoading && userProfile) {
      // Check if user is on waitlist - redirect to waitlist page
      if (userProfile.status === 'waitlist') {
        navigate('/waitlist', { replace: true });
      } else if (userProfile.onboardingComplete === false) {
        // If user needs onboarding, redirect to onboarding
        navigate('/onboarding', { replace: true });
      } else {
        // Redirect to network for active users
        navigate('/network', { replace: true });
      }
    }
  }, [user, userProfile, authIsReady, profileLoading, navigate, isWaitlistSignupInProgress]);

  // Show loading while checking authentication (but not during waitlist signup)
  if (!isWaitlistSignupInProgress && (!authIsReady || (user && profileLoading))) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  // Don't render the landing page if user is authenticated (they'll be redirected)
  // But keep rendering during waitlist signup
  if (user && !isWaitlistSignupInProgress && !showSuccess) {
    return null;
  }





  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="bg-white">
      {/* Header */}
      <header className="absolute inset-x-0 top-0 z-50">
        <nav aria-label="Global" className="flex items-center justify-between p-6 lg:px-8">
          <div className="flex lg:flex-1">
            <a href="#" className="-m-1.5 p-1.5 flex items-center gap-2">
              <span className="sr-only">Burnt</span>
              <img src="/burnt-logo.svg" alt="Burnt Logo" className="h-6" />
              <span className="text-2xl font-bold bg-gradient-to-r from-purple-500 to-purple-700 bg-clip-text text-transparent">burnt</span>
            </a>
          </div>
          <div className="flex lg:hidden">
            <button
              type="button"
              onClick={() => setMobileMenuOpen(true)}
              className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-700"
            >
              <span className="sr-only">Open main menu</span>
              <Bars3Icon aria-hidden="true" className="size-6" />
            </button>
          </div>
          <div className="hidden lg:flex lg:gap-x-12">
            {navigation.map((item) => (
              <button
                key={item.name}
                onClick={() => scrollToSection(item.href.replace('#', ''))}
                className="text-sm/6 font-semibold text-gray-900 hover:text-purple-600 transition-colors"
              >
                {item.name}
              </button>
            ))}
          </div>
          <div className="hidden lg:flex lg:flex-1 lg:justify-end">
            <Link to="/employers" className="text-sm/6 font-semibold text-gray-900 hover:text-purple-600 transition-colors">
              For Employers
            </Link>
          </div>
        </nav>
        <Dialog open={mobileMenuOpen} onClose={setMobileMenuOpen} className="lg:hidden">
          <div className="fixed inset-0 z-50" />
          <DialogPanel className="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-white p-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10">
            <div className="flex items-center justify-between">
              <a href="#" className="-m-1.5 p-1.5 flex items-center gap-2">
                <span className="sr-only">Burnt</span>
                <img src="/burnt-logo.svg" alt="Burnt Logo" className="h-6" />
                <span className="text-2xl font-bold bg-gradient-to-r from-purple-500 to-purple-700 bg-clip-text text-transparent">burnt</span>
              </a>
              <button
                type="button"
                onClick={() => setMobileMenuOpen(false)}
                className="-m-2.5 rounded-md p-2.5 text-gray-700"
              >
                <span className="sr-only">Close menu</span>
                <XMarkIcon aria-hidden="true" className="size-6" />
              </button>
            </div>
            <div className="mt-6 flow-root">
              <div className="-my-6 divide-y divide-gray-500/10">
                <div className="space-y-2 py-6">
                  {navigation.map((item) => (
                    <button
                      key={item.name}
                      onClick={() => {
                        scrollToSection(item.href.replace('#', ''));
                        setMobileMenuOpen(false);
                      }}
                      className="-mx-3 block rounded-lg px-3 py-2 text-base/7 font-semibold text-gray-900 hover:bg-gray-50 w-full text-left"
                    >
                      {item.name}
                    </button>
                  ))}
                </div>
                <div className="py-6">
                  <Link
                    to="/employers"
                    className="-mx-3 block rounded-lg px-3 py-2.5 text-base/7 font-semibold text-gray-900 hover:bg-gray-50"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    For Employers
                  </Link>
                </div>
              </div>
            </div>
          </DialogPanel>
        </Dialog>
      </header>

      {/* Hero Section */}
      <div className="relative isolate px-6 pt-14 lg:px-8">
        <div
          aria-hidden="true"
          className="absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80"
        >
          <div
            style={{
              clipPath:
                'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)',
            }}
            className="relative left-[calc(50%-11rem)] aspect-1155/678 w-144.5 -translate-x-1/2 rotate-30 bg-linear-to-tr from-[#ff80b5] to-[#9089fc] opacity-30 sm:left-[calc(50%-30rem)] sm:w-288.75"
          />
        </div>
        <div className="mx-auto max-w-2xl pt-32 pb-8 sm:pt-48 sm:pb-12 lg:pt-56 lg:pb-16">
          <div className="text-center">
            <h1 className="text-5xl font-semibold tracking-tight text-balance text-gray-900 sm:text-7xl">
              The Private Network for Top Performers
            </h1>
            <p className="mt-8 text-lg font-medium text-pretty text-gray-500 sm:text-xl/8">
              Tired of the noise on other networks? Burnt is a vetted, members-only community for making the connections that actually matter.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-6">
              <button
                onClick={() => scrollToSection('waitlist')}
                className="rounded-md bg-purple-600 px-6 py-3 text-base font-semibold text-white shadow-xs hover:bg-purple-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-600 transition-colors"
              >
                Join the Waitlist
              </button>
              <button
                onClick={() => navigate('/auth')}
                className="text-base font-semibold text-gray-900 hover:text-purple-600 transition-colors"
              >
                Log In <span aria-hidden="true">→</span>
              </button>
            </div>
            <div className="mt-12">
              <p className="text-sm text-gray-600 mb-3 text-center">Already trusted by professionals from:</p>
            </div>
          </div>
        </div>
        
        {/* Company logos infinite slider */}
        <div className="pb-8 overflow-hidden">
          <InfiniteLogoSlider
            width="200px"
            duration={30}
            toRight={true}
            pauseOnHover={true}
            blurBorders={true}
            blurBorderColor={'#f9fafb'}
          >
            {companyLogos.map((company) => (
              <img
                key={company.name}
                src={company.src}
                alt={company.name}
                className={`transition-all hover:scale-105 grayscale hover:grayscale-0 ${
                  ['Deloitte', 'SpaceX', 'Stripe'].includes(company.name) 
                    ? 'w-28 md:w-36 h-auto object-contain' 
                    : 'max-h-14 md:max-h-16 w-auto object-contain'
                }`}
              />
            ))}
          </InfiniteLogoSlider>
        </div>
        
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="mt-0"></div>
          </div>
        </div>
        <div
          aria-hidden="true"
          className="absolute inset-x-0 top-[calc(100%-13rem)] -z-10 transform-gpu overflow-hidden blur-3xl sm:top-[calc(100%-30rem)]"
        >
          <div
            style={{
              clipPath:
                'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)',
            }}
            className="relative left-[calc(50%+3rem)] aspect-1155/678 w-144.5 -translate-x-1/2 bg-linear-to-tr from-[#ff80b5] to-[#9089fc] opacity-30 sm:left-[calc(50%+36rem)] sm:w-288.75"
          />
        </div>
      </div>

      {/* Features Section */}
      <div id="features" className="hidden bg-gray-50 py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-semibold tracking-tight text-gray-900 sm:text-4xl">
            We're building a networking platform for the modern professional.
            </h2>
          
          </div>
          <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none">
            <dl className="grid max-w-xl grid-cols-1 gap-x-8 gap-y-16 lg:max-w-none lg:grid-cols-2">
              {features.map((feature) => (
                <div key={feature.name} className={classNames(
                  'flex flex-col',
                  !feature.isActive ? 'opacity-60' : ''
                )}>
                  <dt className="flex items-center gap-x-3 text-base font-semibold leading-7 text-gray-900">
                    <feature.icon className="h-5 w-5 flex-none text-purple-600" aria-hidden="true" />
                    {feature.name}
                    <span className={classNames(
                      'inline-flex items-center rounded-full px-2 py-1 text-xs font-medium',
                      feature.isActive 
                        ? 'bg-green-50 text-green-700 ring-1 ring-green-600/20'
                        : 'bg-gray-50 text-gray-600 ring-1 ring-gray-500/20'
                    )}>
                      {feature.status}
                    </span>
                  </dt>
                  <dd className="mt-4 flex flex-auto flex-col text-base leading-7 text-gray-600">
                    <p className="flex-auto">{feature.description}</p>
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </div>



      {/* Pricing Section - Hidden for now */}
      {/* 
      <div className="bg-gray-50 py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-4xl text-center">
            <h2 className="text-base/7 font-semibold text-purple-600">Founder Pricing</h2>
            <p className="mt-2 text-4xl font-semibold tracking-tight text-balance text-gray-900 sm:text-5xl">
              Exclusive Access for Early Adopters
            </p>
          </div>
          <p className="mx-auto mt-6 max-w-2xl text-center text-lg font-medium text-pretty text-gray-600 sm:text-xl/8">
            Get VIP access to the future of professional networking with our limited-time founder pricing.
          </p>
          <div className="mx-auto mt-16 max-w-lg">
            <div className="relative bg-gray-900 shadow-2xl rounded-3xl p-8 ring-1 ring-gray-900/10 sm:p-10">
              <h3 className="text-base/7 font-semibold text-purple-400">
                {pricing.name}
              </h3>
              <p className="mt-4 flex items-baseline gap-x-2">
                <span className="text-5xl font-semibold tracking-tight text-white">
                  {pricing.price}
                </span>
                <span className="text-base text-gray-400">/month</span>
              </p>
              <p className="mt-6 text-base/7 text-gray-300">
                {pricing.description}
              </p>
              <ul
                role="list"
                className="mt-8 space-y-3 text-sm/6 text-gray-300 sm:mt-10"
              >
                {pricing.features.map((feature) => (
                  <li key={feature} className="flex gap-x-3">
                    <CheckIcon
                      aria-hidden="true"
                      className="h-6 w-5 flex-none text-purple-400"
                    />
                    {feature}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => scrollToSection('waitlist')}
                className="mt-8 block w-full rounded-md bg-purple-500 px-3.5 py-2.5 text-center text-sm font-semibold text-white shadow-xs hover:bg-purple-400 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-500 sm:mt-10 transition-colors"
              >
                Join Founder Waitlist
              </button>
            </div>
          </div>
        </div>
      </div>
      */}

      {/* Waitlist Signup Section */}
      <div id="waitlist" className="relative bg-gradient-to-br from-gray-50 via-white to-purple-50/30 dark:from-gray-900 dark:via-gray-900 dark:to-purple-950/30 py-24 sm:py-32 overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-x-0 -top-16 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-24">
          <div
            style={{
              clipPath: 'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)',
            }}
            className="relative left-1/2 -z-10 aspect-1155/678 w-144.5 max-w-none -translate-x-1/2 rotate-30 bg-gradient-to-tr from-purple-300/20 to-pink-300/20 opacity-40 sm:left-[calc(50%-40rem)] sm:w-288.75"
          />
        </div>
        
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <div className="mb-8">
              <span className="inline-flex items-center rounded-full bg-purple-100 dark:bg-purple-900/50 px-4 py-2 text-sm font-semibold text-purple-700 dark:text-purple-300 ring-1 ring-purple-700/10 dark:ring-purple-500/30">
                Become a Member
              </span>
            </div>
          </div>
          
          <div className="mx-auto mt-16 max-w-2xl">
            <div className="relative bg-white/80 dark:bg-gray-800/90 backdrop-blur-sm rounded-3xl p-4 sm:p-8 shadow-2xl ring-1 ring-gray-900/5 dark:ring-gray-700/50 min-h-[350px] sm:min-h-[400px] flex flex-col justify-center">
              {showSuccess ? (
                <div className="text-center py-8">
                  <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30 mb-6">
                    <CheckIcon className="h-8 w-8 text-green-600 dark:text-green-400" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">You're on the list! 🎉</h3>
                  <p className="text-lg text-gray-600 dark:text-gray-300 mb-4">Thanks for joining our exclusive founding members waitlist.</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">We'll notify you as soon as your spot is ready.</p>
                </div>
              ) : (
                <div>
                  <WaitlistFlowContainer
                    onFlowComplete={handleWaitlistFlowComplete}
                    onError={handleWaitlistFlowError}
                  />
                  
                  {/* Flow Error */}
                  {flowError && (
                    <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                      <svg className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                      </svg>
                      <div>
                        <p className="text-sm text-red-800">
                          {flowError.includes('already exists') ? (
                            <>
                              An account with this email already exists.{' '}
                              <button
                                onClick={() => navigate('/auth')}
                                className="text-purple-600 hover:text-purple-700 underline font-semibold"
                              >
                                Log in here
                              </button>
                              {' '}instead.
                            </>
                          ) : (
                            flowError
                          )}
                        </p>
                        <button
                          onClick={() => setFlowError('')}
                          className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
                        >
                          Dismiss
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div id="faq" className="bg-gray-50 py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-4xl">
            <h2 className="text-3xl font-semibold tracking-tight text-gray-900 sm:text-4xl text-center mb-16">
              Frequently Asked Questions
            </h2>
            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white rounded-lg shadow-sm">
                  <button
                    className="w-full px-6 py-4 text-left flex justify-between items-center focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-inset"
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  >
                    <span className="font-medium text-gray-900">{faq.question}</span>
                    <ChevronDownIcon 
                      className={classNames(
                        'h-5 w-5 text-gray-500 transition-transform',
                        openFaq === index ? 'rotate-180' : ''
                      )}
                    />
                  </button>
                  {openFaq === index && (
                    <div className="px-6 pb-4">
                      <p className="text-gray-600">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200">
        <div className="mx-auto max-w-7xl px-6 py-12 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center">
            <div className="flex items-center mb-4 sm:mb-0">
              <div className="flex items-center gap-2 mr-3">
                <img src="/burnt-logo.svg" alt="Burnt Logo" className="h-6" />
                <span className="text-lg font-bold bg-gradient-to-r from-purple-500 to-purple-700 bg-clip-text text-transparent">burnt</span>
              </div>
              <span className="text-sm text-gray-500">© 2024 Burnt, Inc. All rights reserved.</span>
            </div>
            <div className="flex space-x-6">
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">
                Privacy Policy
              </a>
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </footer>


    </div>
  );
};

export default NewLandingPage;